"use client";

import {
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import {
  type CSSTransitionComponentProps,
  type TransitionActions,
  type TransitionTimeout,
} from "./types.js";
import {
  DEFAULT_SLIDE_TRANSITION_TIMEOUT,
  useSlideTransition,
} from "./useSlideTransition.js";

// NOTE: Tis is in both `useSlideTransition` and `Slide` since there are no
// reusable types between these two files.
declare module "react" {
  interface CSSProperties {
    "--rmd-slide-duration"?: string | number;
  }
}

/** @since 6.0.0 */
export interface SlideProps
  extends
    HTMLAttributes<HTMLDivElement>,
    CSSTransitionComponentProps,
    TransitionActions {
  ref?: Ref<HTMLDivElement>;

  /**
   * Set this to `true` to animate this slide into view within a
   * `SlideContainer`. When this switches from `true` to `false`, it will
   * animate out.
   */
  active: boolean;

  /** @defaultValue {@link DEFAULT_SLIDE_TRANSITION_DURATION} */
  timeout?: TransitionTimeout;
  children: ReactNode;
}

/**
 * **Client Component**
 *
 * A reasonable default for handling a slide transition using
 * {@link useSlideTransition}.
 *
 * @see {@link https://react-md.dev/components/slide | Slide Demos}
 * @see {@link SlideContainer} for example usage.
 * @since 6.0.0
 */
export function Slide(props: SlideProps): ReactElement | null {
  const {
    ref: nodeRef,
    active,
    appear,
    enter,
    exit,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    className,
    children,
    timeout = DEFAULT_SLIDE_TRANSITION_TIMEOUT,
    temporary = false,
    exitedHidden = true,
    ...remaining
  } = props;

  const { rendered, elementProps } = useSlideTransition({
    nodeRef,
    appear,
    enter,
    exit,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    className,
    timeout,
    temporary,
    transitionIn: active,
    exitedHidden,
  });

  if (!rendered) {
    return null;
  }

  return (
    <div {...remaining} {...elementProps}>
      {children}
    </div>
  );
}
